import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { usePrevious } from '@ocean/hooks'
import { SessionUtils } from '@ocean/utils'
import { UserUtils, EnvironmentUtils, ReferrerUtils } from 'utils'
import { useLazySelector } from 'hooks'
import { loadDictionariesData } from 'core/dictionaries/slices/dictionaries'
import {
  loadClientConfiguration,
  loadNotificationsCounters,
} from 'core/environment/slices/environment'
import { DictionariesLayer } from 'core/layers'
import {
  checkInitialTokenAlive,
  clearSessionInfo,
  setTokenAlive,
} from 'core/session/slices/session'
import {
  runLegacyResultsPushers,
  loadLegacyMenuConfiguration,
} from 'core/staIntegration/slices/staIntegration'
import { loadCustomerConfiguration } from 'core/system/slices/system'
import { getAccountRequested } from 'modules/Auth/slices/auth'

// Internal layer
// Setup private Application part
const InternalLayer: React.FC = ({ children }) => {
  const dispatch = useDispatch()
  const [isEnvironmentConfigurationLoaded, setEnvironmentConfigurationLoaded] = useState(false)
  const { isInitialTokenAlive, environmentError, environmentLoading } = useLazySelector(
    ({ session, environment }) => {
      const { isLoading: environmentLoading, error: environmentError } = environment.configuration
      return {
        isInitialTokenAlive: session.isInitialTokenAlive,
        environmentLoading,
        environmentError,
      }
    }
  )
  const accountInfo = UserUtils.getUserAccountInfo()
  const prevEnvironmentLoading = usePrevious(environmentLoading)
  const locationPath = ReferrerUtils.getLocationPath(useLocation())

  useEffect(() => {
    ReferrerUtils.storeReferrer(locationPath)
  }, [locationPath])

  useEffect(() => {
    const existConfiguration = EnvironmentUtils.getEnvironmentConfiguration()
    const isConfigurationLoaded = prevEnvironmentLoading && !environmentLoading && !environmentError
    if (existConfiguration || isConfigurationLoaded) {
      setEnvironmentConfigurationLoaded(true)
    }
  }, [prevEnvironmentLoading, environmentLoading, environmentError])

  // Check session on page refresh
  useEffect(() => {
    const sessionToken = SessionUtils.getSessionToken()
    const { allowKeepSessionAlive } = EnvironmentUtils.getPermissions('general')
    // allowKeepSessionAlive - undefined immediately after login
    // So allowKeepSessionAlive checked on page refresh
    if (allowKeepSessionAlive) {
      if (sessionToken) {
        dispatch(checkInitialTokenAlive())
      } else {
        dispatch(clearSessionInfo({ needReturnUrl: true }))
      }
    } else {
      dispatch(setTokenAlive(true))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialTokenAlive) {
      if (accountInfo.isDeclarationConfirmed) {
        dispatch(loadClientConfiguration())
        dispatch(getAccountRequested(accountInfo))
        dispatch(loadNotificationsCounters())
        dispatch(loadLegacyMenuConfiguration())
        dispatch(loadDictionariesData())
        dispatch(loadCustomerConfiguration())
        dispatch(runLegacyResultsPushers())
      } else {
        dispatch(clearSessionInfo({ needReturnUrl: true }))
      }
    }
  }, [isInitialTokenAlive]) // eslint-disable-line react-hooks/exhaustive-deps

  if (
    !isInitialTokenAlive ||
    !accountInfo.isDeclarationConfirmed ||
    !isEnvironmentConfigurationLoaded
  )
    return null

  return <DictionariesLayer>{children}</DictionariesLayer>
}

export default InternalLayer
